import React from 'react';
import './JobsList.css';

const JobsList = ({ jobs, onCancelJob }) => {
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  };

  const formatDuration = (start, end) => {
    if (!end) return 'In progress...';
    
    const duration = new Date(end) - new Date(start);
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  const getStatusIcon = (status) => {
    if (status === 'in_progress' || status === 'processing') {
      return (
        <div className="status-spinner">
          <div className="spinner-ring"></div>
        </div>
      );
    } else if (status === 'completed') {
      return (
        <svg className="status-icon completed" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (status === 'failed') {
      return (
        <svg className="status-icon failed" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 6L6 18M6 6l12 12" />
        </svg>
      );
    } else {
      // pending or any other status
      return (
        <svg className="status-icon pending" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
      );
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'in_progress':
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  };

  const handleDownload = async (url, filename) => {
    if (!url) return;
    
    try {
      // Fetch the file content
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      // Get the blob data
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element for downloading
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      
      // Add to DOM, click, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the temporary URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to opening in new tab if download fails
      window.open(url, '_blank');
    }
  };

  if (jobs.length === 0) {
    return (
      <div className="jobs-list">
        <div className="empty-state">
          <div className="empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10"/>
              <path d="M16 16s-1.5-2-4-2-4 2-4 2"/>
              <line x1="9" y1="9" x2="9.01" y2="9"/>
              <line x1="15" y1="9" x2="15.01" y2="9"/>
            </svg>
          </div>
          <h3 className="empty-title">No jobs found</h3>
          <p className="empty-description">
            Launch your first contact rescue job to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-list">
      <div className="jobs-grid">
        {jobs.map(job => (
          <div key={job.id} className={`job-card ${job.status}`}>
            <div className="job-header">
              <div className="job-status">
                {getStatusIcon(job.status)}
                <span className="status-text">
                  {getStatusText(job.status)}
                </span>
              </div>
              <div className="job-header-right">
                <span className={`plan-badge ${job.plan_type === 'CR+' ? 'plan-plus' : 'plan-standard'}`}>
                  {job.plan_type || 'CR'}
                </span>
                <div className="job-id">#{job.id}</div>
              </div>
            </div>
            
            <div className="job-content">
              <div className="job-email">
                <svg className="email-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span className="email-address">{job.email}</span>
              </div>
              
              <div className="job-details">
                <div className="detail-item">
                  <span className="detail-label">Started:</span>
                  <span className="detail-value">{formatDate(job.createdAt)}</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Duration:</span>
                  <span className="detail-value">
                    {formatDuration(job.createdAt, job.completedAt)}
                  </span>
                </div>
                
                {job.status === 'completed' && (
                  <div className="detail-item contacts-found">
                    <span className="detail-label">Contacts Found:</span>
                    <span className="detail-value highlight">
                      {job.contactsFound.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {job.status === 'completed' && (job.file_result || job.file_emails) && (
              <div className="download-section">
                <div className="download-buttons">
                  {job.file_result && (
                    <button 
                      className="download-btn"
                      onClick={() => handleDownload(job.file_result, `contacts-${job.id}.csv`)}
                      title="Download Contact Results"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Contacts
                    </button>
                  )}
                  {job.file_emails && (
                    <button 
                      className="download-btn"
                      onClick={() => handleDownload(job.file_emails, `emails-${job.id}.csv`)}
                      title="Download Email Results"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7,10 12,15 17,10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                      Emails
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {(job.status === 'in_progress' || job.status === 'processing' || job.status === 'pending') && (
              <div className="progress-section">
                <div className="progress-info">
                  <span className="progress-text">Progress</span>
                  <span className="progress-percentage">{job.progress || 0}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${job.progress || 0}%` }}
                  ></div>
                </div>
                {job.status === 'pending' && onCancelJob && (
                  <button 
                    className="cancel-btn"
                    onClick={() => onCancelJob(job.id)}
                    title="Cancel this job"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    Cancel Job
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default JobsList;